import * as React from "react";
import { cn } from "@/lib/utils";

export interface TimelineStep {
  name: string; // e.g. "Plan", "Edit", "Install", "Test", "Review"
  duration?: string; // e.g. "02:10", "11:40", "–"
  status?: "pending" | "running" | "completed" | "failed";
  startPct?: number; // 0 to 100
  widthPct?: number; // 0 to 100
}

export interface RunTimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  steps?: TimelineStep[];
  totalDuration?: string;
  title?: string;
}

const defaultTimelineSteps: TimelineStep[] = [
  { name: "Plan", duration: "02:10", status: "completed", startPct: 0, widthPct: 8 },
  { name: "Edit", duration: "11:40", status: "completed", startPct: 8, widthPct: 36 },
  { name: "Install", duration: "01:05", status: "completed", startPct: 44, widthPct: 8 },
  { name: "Test", duration: "08:30", status: "completed", startPct: 52, widthPct: 26 },
  { name: "Review", duration: "–", status: "pending", startPct: 78, widthPct: 22 },
];

/**
 * RunTimeline renders the phase lifecycle for a run:
 * Plan, Edit, Install, Test, and Review with durations and visual progress bar.
 */
export function RunTimeline({
  steps = defaultTimelineSteps,
  totalDuration,
  title = "Run timeline",
  className,
  ...props
}: RunTimelineProps) {
  return (
    <div className={cn("flex flex-col gap-3", className)} {...props}>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{title}</span>
        {totalDuration ? (
          <span className="font-mono text-muted-foreground">{totalDuration}</span>
        ) : null}
      </div>

      <ol className="flex flex-col gap-2">
        {steps.map((step, index) => {
          const start = step.startPct ?? (index / steps.length) * 100;
          const width = step.widthPct ?? (1 / steps.length) * 100;
          const isFailed = step.status === "failed";
          const isRunning = step.status === "running";
          const isCompleted = step.status === "completed";
          const statusLabel =
            step.status === "completed"
              ? "Completed"
              : step.status === "running"
                ? "Running"
                : step.status === "failed"
                  ? "Failed"
                  : step.status === "pending"
                    ? "Pending"
                    : "Not started";

          return (
            <li
              key={step.name}
              className="grid grid-cols-[5rem_1fr_4rem_5rem] items-center gap-3 text-xs"
            >
              <span className="font-medium text-foreground truncate">{step.name}</span>

              {/* Progress track */}
              <div
                className="relative h-2 w-full overflow-hidden rounded-full bg-muted"
                role="img"
                aria-label={`${step.name}: ${statusLabel}`}
              >
                <div
                  aria-hidden
                  className={cn(
                    "absolute inset-y-0 rounded-full transition-all",
                    isFailed
                      ? "bg-danger"
                      : isRunning
                        ? "animate-pulse bg-info"
                        : isCompleted
                          ? "bg-primary"
                          : "bg-muted-foreground/30",
                  )}
                  style={{
                    left: `${Math.max(0, Math.min(100, start))}%`,
                    width: `${Math.max(2, Math.min(100, width))}%`,
                  }}
                />
              </div>

              <span className="text-right font-mono text-muted-foreground">
                {step.duration ?? "–"}
              </span>

              <span className="text-right text-muted-foreground">{statusLabel}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
